import { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ChangeEvent, Dispatch, SetStateAction } from 'react';
import { orderBy } from 'lodash';

export interface IEnvironment {
  environment: string;
  action?: 'publish' | 'unpublish' | 'scheduled';
  scheduled_at?: string;
  time?: string;
  user?: string;
  locale: string;
}

export interface IVersion {
  _version: number;
  _version_name?: {
    title: string;
    updated_at?: string;
    updated_by?: string;
  };
  locale: string;
}

export const getEnvColor = (uid: string, environmentsList: Array<{ uid: string; name: string; color: string }>) => {
  const env = environmentsList?.find(env => env.uid === uid);
  return env?.color || '#A9B6CB';
};

export const getEnvName = (uid: string, environmentsList: Array<{ uid: string; name: string; color: string }>) => {
  const env = environmentsList?.find(env => env.uid === uid);
  return env?.name || uid;
};

export const getPillText = (env: IEnvironment): string | null => {
  const currentDate = new Date()
  const scheduleAtDate = env?.scheduled_at ? new Date(env.scheduled_at) : null
  const scheduledDatePassed = scheduleAtDate && (currentDate > scheduleAtDate)

  // If publish_details array is not empty (env exists) and no scheduled_at, it's published
  if (env && !env.scheduled_at) {
    return "Published"
  }

  // Handle scheduled actions
  if (env.scheduled_at) {
    if (env.action === 'publish') {
      return scheduledDatePassed ? "Scheduled for Publish Failed" : "Scheduled for Publish"
    }
    if (env.action === 'unpublish') {
      return scheduledDatePassed ? "Scheduled for Unpublish Failed" : "Scheduled for Unpublish"
    }
  }

  return null
};

export const getVersionDisplayName = (version: IVersion) => {
  return version._version_name?.title || `Version ${version._version}`;
};

export const handleVersionNameChange = (
  e: ChangeEvent<HTMLInputElement>,
  setVersionName: Dispatch<SetStateAction<string>>
) => {
  const newName = e.target.value.trim();
  setVersionName(newName);
  return newName;
};

export const handleVersionNameKeyDown = (
  e: ReactKeyboardEvent<HTMLInputElement>,
  handleSave: () => void,
  setIsEditing: Dispatch<SetStateAction<boolean>>,
  currentVersionName: string,
  setVersionName: Dispatch<SetStateAction<string>>
) => {
  if (e.key === 'Enter') {
    e.preventDefault();
    e.stopPropagation();
    if (!currentVersionName.trim()) {
      // If empty, revert to previous name
      setVersionName(currentVersionName);
    }
    handleSave();
  } else if (e.key === 'Escape') {
    setIsEditing(false);
  }
};

export const handleVersionClickKeyDown = (e: ReactKeyboardEvent, versionOnClick: Function | undefined, handleVersionClick: (e: ReactMouseEvent) => void) => {
  if (e.key === 'Enter' && versionOnClick) {
    handleVersionClick(e as any);
  }
};

export const handleEditModeKeyDown = (e: ReactKeyboardEvent, enableEditMode: () => void) => {
  if (e.key === 'Enter') {
    enableEditMode();
  }
};

export const handleCompareClick = (
  e: MouseEvent,
  compareVersion: Function | undefined,
  version: IVersion,
  defaultSelected: IVersion | undefined
) => {
  e.stopPropagation();
  if (compareVersion) {
    compareVersion({
      current: version._version,
      compareTo: defaultSelected ? defaultSelected._version : version._version
    });
  }
};

export const handleCompareKeyDown = (e: ReactKeyboardEvent, handleCompareClickFn: (e: ReactMouseEvent) => void) => {
  if (e.key === 'Enter') {
    e.stopPropagation();
    handleCompareClickFn(e as any);
  }
};

export const getLocaleName = (uid: any, locales: any) => {
    if (uid && locales && locales.length) {
        const localName = locales.find((iLocale: any) => iLocale.code === uid);
        if (localName && localName.name) {
            return localName.name;
        }
        return uid
    };
    return ''
};

// Constants
export const BUTTON_ATTRIBUTES = {
  tabIndex: 0,
  role: 'button'
} as const;

// Types
export type InputChangeEvent = React.ChangeEvent<HTMLInputElement>;
export type SetStateString = React.Dispatch<React.SetStateAction<string>>;
export type KeyboardEvent = ReactKeyboardEvent<HTMLDivElement>;
export type MouseEvent = ReactMouseEvent<HTMLDivElement>;

export const sortEnvironmentsByTime = (environments: IEnvironment[]) => {
  return orderBy(environments, ['time'], ['desc']);
};

